import { useState } from "react";
import SVG from "react-inlinesvg";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Lottie } from "@remo-co/ui-core/src/components/Lottie";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { DialogActions } from "@remo-co/ui-core/src/components/DialogActions";
import { useShuffleAttendees } from "modules/shuffleAttendees/hooks/useShuffleAttendees";
import { useI18n } from "i18n";
import { useSelector } from "react-redux";
import { selectCurrentEventId } from "modules/event/selectors";
import { Close } from "@remo-co/ui-core/src/icons/Close";
import { DialogTitle } from "@remo-co/ui-core/src/components/DialogTitle";
import logger from "logging/logger";
import { selectUserId } from "modules/auth/redux/selectors";
import { IconButtonWithTooltip } from "@remo-co/ui-core/src/components/IconButtonWithTooltip";
import { Errors, trackError } from "modules/monitoring";
import { useAppDispatch } from "store/hooks";
import ShuffleAnimation from "../../assets/shuffle_anim.json";
import ShuffleIcon from "./assets/shuffle_icon.svg";
import { ShuffleGroupSize } from "../ShuffleGroupSize";
import { styles } from "./styles";
import { hideShuffleAttendeesDialog } from "../../redux/actions";

interface ShuffleAttendeesDialogComponentProps
  extends ShuffleAttendeesDialogProps {
  handleClose: () => void;
  startShuffling: () => Promise<void>;
}

export const ShuffleAttendeesDialogComponent = ({
  handleClose,
  setTargetGroupSize,
  targetGroupSize,
  startShuffling,
}: ShuffleAttendeesDialogComponentProps) => {
  const { t } = useI18n(["event"]);
  const [loading, setLoading] = useState(false);

  const handleStartShuffle = async () => {
    try {
      setLoading(true);
      await startShuffling();
    } catch (error) {
      setLoading(false);
      trackError(error, {
        label: Errors.SHUFFLE_START,
      });
    }
  };

  return (
    <Dialog onClose={handleClose} fullScreen={false} open>
      <DialogTitle sx={styles.dialogTitle}>
        <IconButtonWithTooltip
          id="modal-close-button"
          data-testid="modal-close-button"
          title={t("common:button.close")}
          onClick={handleClose}
          size="small"
        >
          <Close sx={styles.closeIcon} />
        </IconButtonWithTooltip>
      </DialogTitle>
      <Box sx={styles.container}>
        <Box sx={styles.image}>
          <Lottie animationData={ShuffleAnimation} />
        </Box>
        <Box sx={styles.content}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={styles.icon}>
              <SVG id="shuffle-icon" src={ShuffleIcon} />
            </Box>
            <Typography variant="h3">{t("event:shuffle.guests")}</Typography>
          </Box>
          <Typography my={1} px={3} variant="body1">
            {t("event:instant.boost")}
          </Typography>
          <Box>
            <Typography variant="body1" mb={1}>
              {t("event:shuffle.target.group.size")}
            </Typography>
            <ShuffleGroupSize
              targetGroupSize={targetGroupSize}
              setTargetGroupSize={setTargetGroupSize}
            />
          </Box>
          <Typography mt={1} variant="caption">
            {t("event:tables.will.be.unlocked")}
          </Typography>
        </Box>
      </Box>
      <DialogActions sx={styles.dialogActions}>
        <Button
          loading={loading}
          size="lg"
          variant="secondary"
          fullWidth
          data-testid="start-shuffle-button"
          onClick={handleStartShuffle}
          disabled={targetGroupSize === null}
        >
          <span>{t("event:button.shuffle")}</span>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ShuffleAttendeesDialogProps {
  setTargetGroupSize: (targetGroupSize: number | null) => void;
  targetGroupSize: number | null;
}

export const ShuffleAttendeesDialog = ({
  setTargetGroupSize,
  targetGroupSize,
}: ShuffleAttendeesDialogProps) => {
  const currentEventId = useSelector(selectCurrentEventId);
  const userId = useSelector(selectUserId);
  const { addShuffleAttendeesDoc } = useShuffleAttendees();
  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch(hideShuffleAttendeesDialog());
  };

  const startShuffle = async () => {
    if (!currentEventId || !userId) {
      logger.warn(`[Shuffle] tried shuffling with null value`, {
        userId,
        currentEventId,
      });
      return;
    }
    await addShuffleAttendeesDoc(currentEventId, {
      initiatedBy: userId,
      targetGroupSize,
    });
  };

  return (
    <ShuffleAttendeesDialogComponent
      handleClose={handleClose}
      setTargetGroupSize={setTargetGroupSize}
      targetGroupSize={targetGroupSize}
      startShuffling={startShuffle}
    />
  );
};
